import { Fragment } from 'react';
import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import { Input } from '@components/Input';
import Button from '@components/Button';
import { Select } from '@components/Select';
import PhoneInput from '@components/PhoneInput';
import { useTranslation } from '@hooks';
import { ChatType } from '@constants/companies';
import { ProfileName, ProfileType } from '@constants/integrations';
import { DeleteIcon } from 'assets/images/icons';
import { setInitialPhoneNumberToForm } from '@helpers/phone';

export const Form = styled('div')({
    maxWidth: 698,
    display: 'grid',
    columnGap: 24,
    rowGap: 16,
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
        maxWidth: 'none',
    },
});

const StyledIconButton = styled(IconButton)({
    width: 32,
    height: 32,
    transform: 'translateY(4px)',
    padding: '4px',
    '& svg path': {
        fill: 'rgba(0, 0, 0, 0.6)',
    },
});

const StyledPhoneInput = styled(PhoneInput)({
    paddingBottom: 16,
});

const MockDiv = styled('div')({
    '@media (max-width: 1024px)': {
        display: 'none',
    },
});

const Contacts = ({ accountLocale }) => {
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'locationWebsites',
    });
    const locationWebsites = watch('locationWebsites');
    const watchChatLinkAdded = watch('chatLinkAdded');

    const getPlatformsOptions = currentIndex => {
        const allOptions = [
            { label: ProfileType[ProfileName.GoogleOfficial], value: ProfileName.GoogleOfficial },
            { label: ProfileType[ProfileName.Apple], value: ProfileName.Apple },
            { label: ProfileType[ProfileName.FacebookOfficial], value: ProfileName.FacebookOfficial },
        ];

        const selectedTypes = (locationWebsites || [])
            .map((website, index) => (index !== currentIndex ? website?.type : null))
            .filter(Boolean);
        return allOptions.filter(option => !selectedTypes.includes(option.value));
    };

    return (
        <>
            <Form>
                <div style={{ display: 'grid', gridGap: '8px', gridTemplateColumns: 'calc(100% - 73px) 65px' }}>
                    <Controller
                        control={control}
                        name="phoneNumber"
                        render={({ field, fieldState: { error } }) => (
                            <StyledPhoneInput
                                placeholder=""
                                label={t('SettingsCompanies.primaryPhone')}
                                inputProps={{ name: 'phoneNumber' }}
                                value={field.value}
                                autoFormat
                                onChange={value => {
                                    setValue('phoneNumber', value, { shouldValidate: true, shouldDirty: true });
                                }}
                                error={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="phoneExtension"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsCompanies.phoneExtension')}
                                fullWidth
                                error={!!error}
                            />
                        )}
                    />
                </div>
                <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.email')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="website"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsCompanies.website')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <MockDiv />
                {fields.map((field, index) => (
                    <Fragment key={field.id}>
                        <Controller
                            key={`url-${field.id}-${index}`}
                            control={control}
                            name={`locationWebsites[${index}].url`}
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.customURL')}
                                    fullWidth
                                    required
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                />
                            )}
                        />
                        <div style={{ display: 'grid', gridGap: '8px', gridTemplateColumns: 'calc(100% - 40px) 32px' }}>
                            <Controller
                                key={`url-${field.id}-${index}`}
                                control={control}
                                name={`locationWebsites[${index}].type`}
                                render={({ field, ref }) => (
                                    <Select
                                        {...field}
                                        ref={ref}
                                        onChange={({ target: { value } }) => {
                                            setValue(`locationWebsites[${index}].type`, value, {
                                                shouldDirty: true,
                                            });
                                        }}
                                        label={t('common.platform')}
                                        fullWidth
                                        options={getPlatformsOptions(index)}
                                    />
                                )}
                            />
                            <StyledIconButton onClick={() => remove(index)}>
                                <DeleteIcon />
                            </StyledIconButton>
                        </div>
                    </Fragment>
                ))}
                <Button
                    variant="outlined"
                    sx={{ width: 'max-content' }}
                    startIcon={<AddIcon />}
                    disabled={fields.length === 3}
                    onClick={() =>
                        append({
                            url: '',
                            type:
                                locationWebsites.length === 0
                                    ? ProfileName.GoogleOfficial
                                    : getPlatformsOptions(locationWebsites.length)[0].value,
                        })
                    }
                >
                    {t('SettingsCompanies.addCustomURL')}
                </Button>
                <MockDiv />
                {watchChatLinkAdded && (
                    <>
                        <Controller
                            control={control}
                            name="chatLink"
                            render={({ field, fieldState: { error } }) => (
                                <PhoneInput
                                    placeholder=""
                                    label={t('common.phoneNumber')}
                                    value={field.value}
                                    autoFormat
                                    onChange={value => {
                                        setValue('chatLink', value, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                    error={error ? t(error.message) : null}
                                    required={watchChatLinkAdded}
                                />
                            )}
                        />
                        <div style={{ display: 'grid', gridGap: '8px', gridTemplateColumns: 'calc(100% - 40px) 32px' }}>
                            <Controller
                                control={control}
                                name="chatType"
                                render={({ field, ref }) => (
                                    <Select
                                        {...field}
                                        ref={ref}
                                        onChange={({ target: { value } }) =>
                                            setValue('chatType', value, { shouldDirty: true })
                                        }
                                        label={t('common.type')}
                                        fullWidth
                                        options={[
                                            { label: 'SMS', value: ChatType.SMS },
                                            { label: 'WhatsApp', value: ChatType.WhatsApp },
                                        ]}
                                    />
                                )}
                            />
                            <StyledIconButton
                                onClick={() => {
                                    setValue('chatLinkAdded', false, { shouldValidate: true });
                                    setValue('chatLink', setInitialPhoneNumberToForm('', accountLocale), {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                    });
                                    setValue('chatType', ChatType.SMS, { shouldDirty: true });
                                }}
                            >
                                <DeleteIcon />
                            </StyledIconButton>
                        </div>
                    </>
                )}
            </Form>
            <Button
                variant="outlined"
                sx={{ width: 'max-content', marginTop: '8px' }}
                startIcon={<AddIcon />}
                disabled={watchChatLinkAdded}
                onClick={() => setValue('chatLinkAdded', true, { shouldValidate: true })}
            >
                {t('SettingsCompanies.addChat')}
            </Button>
        </>
    );
};

export default Contacts;
